// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { Ctx } from '../main.ts';
import type { FlowRow } from '../data.ts';
import { stackBands } from '../utils/layout.ts';
import { partyColor, GROUP_META } from '../parties.ts';
import { fmtInt, fmtPct, esc, escTip } from '../utils/format.ts';
import { term } from '../glossary.ts';

interface Node { key: string; label: string; color: string; total: number; toALP: number; toCOA: number }

export function renderPreferences(root: HTMLElement, ctx: Ctx): void {
  const flows = ctx.data.flows;
  const grand = flows.reduce((a, f) => a + f.total, 0);
  const threshold = grand * 0.012;

  // Fold tiny parties into one "Other minor parties" node.
  const big = flows.filter((f) => f.total >= threshold);
  const small = flows.filter((f) => f.total < threshold);
  const nodes: Node[] = big.map((f) => ({
    key: f.ab, label: f.name.length > 26 ? f.name.slice(0, 25) + '…' : f.name,
    color: partyColor(f.ab, f.group), total: f.total, toALP: f.toALP, toCOA: f.toCOA,
  }));
  if (small.length) {
    nodes.push({
      key: 'OTHER', label: `Other (${small.length} parties)`, color: '#94a3b8',
      total: small.reduce((a, f) => a + f.total, 0),
      toALP: small.reduce((a, f) => a + f.toALP, 0),
      toCOA: small.reduce((a, f) => a + f.toCOA, 0),
    });
  }
  nodes.sort((a, b) => b.total - a.total);

  const sumALP = nodes.reduce((a, n) => a + n.toALP, 0);
  const sumCOA = nodes.reduce((a, n) => a + n.toCOA, 0);
  const alpShare = (sumALP / (sumALP + sumCOA)) * 100;

  // Geometry
  const W = 1000, H = Math.max(560, nodes.length * 46 + 60);
  const padT = 30, padB = 30, nodeW = 15;
  const leftX = 220, rightX = W - 220 - nodeW;
  const avail = H - padT - padB;
  const gapL = 8;
  const leftBands = stackBands(nodes.map((n) => n.total), avail, gapL).map((b) => ({ y: b.y + padT, h: b.h }));
  const rightBands = stackBands([sumALP, sumCOA], avail, 40).map((b) => ({ y: b.y + padT, h: b.h }));
  const rightCursor = [rightBands[0].y, rightBands[1].y]; // ALP, COA

  const path = (x0: number, y0a: number, y0b: number, x1: number, y1a: number, y1b: number): string => {
    const mx = (x0 + x1) / 2;
    return `M${x0},${y0a} C${mx},${y0a} ${mx},${y1a} ${x1},${y1a} L${x1},${y1b} C${mx},${y1b} ${mx},${y0b} ${x0},${y0b} Z`;
  };

  const links: string[] = [];
  const nodeRects: string[] = [];
  const labels: string[] = [];
  nodes.forEach((n, i) => {
    const band = leftBands[i];
    const hALP = (n.toALP / n.total) * band.h;
    const hCOA = band.h - hALP;
    // Link to ALP (top sub-band)
    {
      const ry0 = rightCursor[0];
      const ry1 = ry0 + hALP;
      rightCursor[0] = ry1;
      links.push(`<path d="${path(leftX + nodeW, band.y, band.y + hALP, rightX, ry0, ry1)}" fill="${GMcolor('ALP')}" fill-opacity="0.34" class="flow-link" data-tip="${escTip(`${n.label} → Labor\n${fmtInt(n.toALP)} preferences (${fmtPct((n.toALP / n.total) * 100)} of this party)`)}"/>`);
    }
    // Link to Coalition (bottom sub-band)
    {
      const ry0 = rightCursor[1];
      const ry1 = ry0 + hCOA;
      rightCursor[1] = ry1;
      links.push(`<path d="${path(leftX + nodeW, band.y + hALP, band.y + band.h, rightX, ry0, ry1)}" fill="${GMcolor('COA')}" fill-opacity="0.34" class="flow-link" data-tip="${escTip(`${n.label} → Coalition\n${fmtInt(n.toCOA)} preferences (${fmtPct((n.toCOA / n.total) * 100)} of this party)`)}"/>`);
    }
    nodeRects.push(`<rect x="${leftX}" y="${band.y}" width="${nodeW}" height="${Math.max(1, band.h)}" rx="2" fill="${n.color}" data-tip="${escTip(`${n.label}\n${fmtInt(n.total)} preferences distributed\n${fmtPct((n.toALP / n.total) * 100)} to Labor · ${fmtPct((n.toCOA / n.total) * 100)} to Coalition`)}"/>`);
    labels.push(`<text x="${leftX - 8}" y="${band.y + band.h / 2}" text-anchor="end" dominant-baseline="middle" font-size="13" fill="#0f1c2e">${esc(n.label)}</text>`);
  });

  // Right nodes
  const rightNode = (idx: number, key: 'ALP' | 'COA', label: string, val: number) => {
    const b = rightBands[idx];
    return `<rect x="${rightX}" y="${b.y}" width="${nodeW}" height="${Math.max(1, b.h)}" rx="2" fill="${GMcolor(key)}" data-tip="${escTip(`${label}\n${fmtInt(val)} preferences received`)}"/>
      <text x="${rightX + nodeW + 8}" y="${b.y + b.h / 2}" dominant-baseline="middle" font-size="14" font-weight="700" fill="${GMcolor(key)}">${label}</text>
      <text x="${rightX + nodeW + 8}" y="${b.y + b.h / 2 + 17}" dominant-baseline="middle" font-size="12" fill="#475569">${fmtInt(val)}</text>`;
  };

  root.innerHTML = `
    <div class="view-head">
      <h2>Where the preferences went</h2>
      <p class="subtitle">Australia uses full ${term('preferences', 'preferential voting')}. When a minor-party or independent candidate is excluded, their ballots flow on. This is how every party's preferences split between Labor and the Coalition at the final ${term('tpp', 'two-party')} count.</p>
    </div>

    <div class="stat-grid" style="margin-bottom:var(--sp-xl)">
      <div class="stat" style="border-left-color:var(--grn)"><div class="k">Greens preferences to Labor</div><div class="v" style="color:var(--grn)">${fmtPct(pct(flows, 'GRN', 'alp'))}</div><div class="d">the biggest single bloc of preferences</div></div>
      <div class="stat" style="border-left-color:var(--oth)"><div class="k">One Nation preferences to Coalition</div><div class="v" style="color:var(--oth)">${fmtPct(pct(flows, 'ON', 'coa'))}</div><div class="d">right-of-centre minor parties favour the Coalition</div></div>
      <div class="stat" style="border-left-color:var(--ind)"><div class="k">Independent preferences to Labor</div><div class="v" style="color:var(--ind)">${fmtPct(pct(flows, 'IND', 'alp'))}</div><div class="d">independents' ballots leaned Labor overall</div></div>
      <div class="stat" style="border-left-color:var(--navy)"><div class="k">All preferences to Labor</div><div class="v">${fmtPct(alpShare)}</div><div class="d">across every excluded candidate nationally</div></div>
    </div>

    <div class="panel">
      <div class="panel-title">Preference flow — minor parties to the majors</div>
      <div class="panel-sub">Band width is the number of preferences. Hover any band for the exact figure.</div>
      <div class="chart-wrap">
        <svg class="chart" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Sankey diagram of preference flows from minor parties to Labor and the Coalition">
          <g>${links.join('')}</g>
          <g>${nodeRects.join('')}</g>
          <g>${rightNode(0, 'ALP', 'Labor', sumALP)}${rightNode(1, 'COA', 'Coalition', sumCOA)}</g>
          <g>${labels.join('')}</g>
        </svg>
      </div>
      <p class="note">Only preferences that were actually distributed are shown — in a seat won on first preferences, or where a party's candidate reached the final two, those votes never flow. Figures are national totals from the AEC's two-party-preferred flow of preferences.</p>
    </div>`;
}

function GMcolor(k: 'ALP' | 'COA'): string { return GROUP_META[k].color; }

function pct(flows: FlowRow[], ab: string, dir: 'alp' | 'coa'): number {
  const f = flows.find((x) => x.ab === ab);
  if (!f) return 0;
  return dir === 'alp' ? f.toALPPct : f.toCOAPct;
}
